<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { ArrowRightLeft } from 'lucide-vue-next'
import { CurrencyIcon } from '~/components/ui/currency-icon'
import type { ICurrentRatesData } from '~/components/widget/bank-page/rates/wrapper'

interface BankRate {
  buy: number
  sell: number
}

interface Props {
  bankId: number
  bankName: string
  ratesData: ICurrentRatesData
  ratesStatus: 'idle' | 'pending' | 'success' | 'error'
}

const props = defineProps<Props>()

const currencyList = useCurrencyStore().list

const availableCurrencies = ['kgs', 'usd', 'eur', 'rub', 'kzt']

const fromCurrency = ref('usd')
const toCurrency = ref('kgs')
const fromAmount = ref('100')
const toAmount = ref('')
const toAmountRaw = ref(0)
const rateType = ref('regular')

const currencyOptions = computed(() => {
  const rateKeys = new Set([
    'kgs',
    ...Object.keys(rateType.value === 'regular' ? currentRegularRates.value : currentCashlessRates.value)
  ])
  return availableCurrencies
    .filter((code) => rateKeys.has(code))
    .map((code) => {
      const upperCode = code.toUpperCase()
      const currencyInfo = currencyList.find((c) => c.code === upperCode)
      return {
        value: code,
        label: upperCode,
        title: currencyInfo?.title || upperCode,
        symbol: currencyInfo?.symbol || upperCode
      }
    })
})

const fromCurrencyOptions = computed(() =>
  currencyOptions.value.filter((o) => o.value !== toCurrency.value)
)

const toCurrencyOptions = computed(() =>
  currencyOptions.value.filter((o) => o.value !== fromCurrency.value)
)

// Normalize rate data
const normalize = (
  input?: Record<string, { buy: string; sell: string }> | null
): Record<string, BankRate> => {
  if (!input) return {}
  return Object.fromEntries(
    Object.entries(input).map(([currency, rate]) => [
      currency.toLowerCase(),
      {
        buy: parseFloat(rate.buy),
        sell: parseFloat(rate.sell)
      }
    ])
  )
}

const currentRegularRates = computed(() => normalize(props.ratesData?.currentRegularRates))

const currentCashlessRates = computed(() => normalize(props.ratesData?.currentCashlessRates))

const currentRates = computed(() =>
  rateType.value === 'regular' ? currentRegularRates.value : currentCashlessRates.value
)

const getCurrentRate = (fromCode: string, toCode: string): number => {
  if (fromCode === toCode) return 1

  const rates = currentRates.value

  if (fromCode !== 'kgs' && toCode === 'kgs') {
    // Converting foreign currency to KGS - you're selling foreign currency to bank
    const rate = rates[fromCode]
    return rate?.buy || 1
  }

  if (fromCode === 'kgs' && toCode !== 'kgs') {
    // Converting KGS to foreign currency - you're buying foreign currency from bank
    const rate = rates[toCode]
    return rate?.sell ? 1 / rate.sell : 1
  }

  // Cross-currency conversion through KGS
  const fromToKgs = getCurrentRate(fromCode, 'kgs')
  const kgsToTarget = getCurrentRate('kgs', toCode)
  return fromToKgs * kgsToTarget
}

const convertCurrency = () => {
  const amount = parseFloat(fromAmount.value) || 0
  const rate = getCurrentRate(fromCurrency.value, toCurrency.value)
  const result = amount * rate
  const toCurrencyId = useGetCurrencyIdByCode(toCurrency.value)
  toAmountRaw.value = result
  // The result is always a real number here, so the format never returns null.
  toAmount.value = useCurrencyFormat(result, toCurrencyId, false) ?? ''
}

const isSwapping = ref(false)

const swapCurrencies = () => {
  isSwapping.value = true
  const savedToAmount = toAmountRaw.value
  const tempFrom = fromCurrency.value
  const tempTo = toCurrency.value
  fromCurrency.value = tempTo
  toCurrency.value = tempFrom
  fromAmount.value = savedToAmount.toFixed(2)
  nextTick(() => {
    convertCurrency()
    isSwapping.value = false
  })
}

watch(rateType, () => {
  const available = currencyOptions.value.map((o) => o.value)
  if (!available.includes(fromCurrency.value)) {
    fromCurrency.value = available.find((c) => c !== 'kgs') || 'kgs'
  }
  if (!available.includes(toCurrency.value)) {
    toCurrency.value = 'kgs'
  }
})


const hasCashlessData = computed(() =>
  Object.values(currentCashlessRates.value).some((r) => r.buy > 0 || r.sell > 0)
)

const hasRegularData = computed(() =>
  Object.values(currentRegularRates.value).some((r) => r.buy > 0 || r.sell > 0)
)

if (!hasRegularData.value && hasCashlessData.value) {
  rateType.value = 'cashless'
}

watch([fromCurrency, toCurrency, fromAmount, rateType], () => {
  if (!isSwapping.value) convertCurrency()
}, {
  immediate: true
})
</script>

<template>
  <div class="space-y-6">
    <p class="text-gray-600">
      {{ $t('widget.bank_page.rates_converter.description', { bankName }) }}
    </p>

    <div class="rounded-lg border bg-white p-6">
      <div class="space-y-4">
        <!-- Desktop Layout -->
        <div class="hidden sm:block">
          <!-- Labels Row -->
          <div class="flex items-center gap-4">
            <div class="flex-1">
              <Label for="from-amount">{{ $t('common.i_have') }}</Label>
            </div>
            <div class="w-12" />
            <div class="flex-1">
              <Label for="to-amount">{{ $t('common.will_receive') }}</Label>
            </div>
          </div>

          <!-- Input Row -->
          <div class="mt-2 flex items-center gap-4">
            <!-- From Currency -->
            <div class="flex-1">
              <div
                class="flex h-[2.25rem] items-center gap-0 overflow-hidden rounded-lg border border-gray-300 ring-offset-2 focus-within:ring-2 focus-within:ring-blue-600"
              >
                <Input
                  id="from-amount"
                  v-model="fromAmount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0"
                  class="flex-1 rounded-none border-0 bg-transparent focus:ring-0 focus-visible:ring-0"
                />
                <Select v-model="fromCurrency">
                  <SelectTrigger
                    class="w-[100px] rounded-none border-0 border-l bg-transparent focus:ring-0 focus-visible:ring-0"
                    :aria-label="$t('common.currency')"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      v-for="currency in fromCurrencyOptions"
                      :key="currency.value"
                      :value="currency.value"
                    >
                      <div class="flex items-center gap-2">
                        <CurrencyIcon :code="currency.label" size="sm" />
                        {{ currency.label }}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <!-- Swap Button -->
            <div class="flex w-12 justify-center">
              <Button
                variant="outline-gray"
                size="icon"
                class="rounded-full"
                :aria-label="$t('common.swap_currencies_and_amounts')"
                @click="swapCurrencies"
              >
                <ArrowRightLeft class="h-4 w-4" />
              </Button>
            </div>

            <!-- To Currency -->
            <div class="flex-1">
              <div
                class="flex h-[2.25rem] items-center gap-0 overflow-hidden rounded-lg border border-gray-300 bg-gray-50"
              >
                <Input
                  id="to-amount"
                  v-model="toAmount"
                  type="text"
                  readonly
                  class="flex-1 rounded-none border-0 bg-transparent focus:ring-0 focus-visible:ring-0"
                />
                <Select v-model="toCurrency">
                  <SelectTrigger
                    class="w-[100px] rounded-none border-0 border-l bg-transparent focus:ring-0 focus-visible:ring-0"
                    :aria-label="$t('common.currency')"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      v-for="currency in toCurrencyOptions"
                      :key="currency.value"
                      :value="currency.value"
                    >
                      <div class="flex items-center gap-2">
                        <CurrencyIcon :code="currency.label" size="sm" />
                        {{ currency.label }}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile Layout -->
        <div class="block sm:hidden">
          <!-- From Currency -->
          <div class="space-y-2">
            <Label for="from-amount-mobile">{{ $t('common.i_have') }}</Label>
            <div
              class="flex h-[2.25rem] items-center gap-0 overflow-hidden rounded-lg border border-gray-300 ring-offset-2 focus-within:ring-2 focus-within:ring-blue-600"
            >
              <Input
                id="from-amount-mobile"
                v-model="fromAmount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0"
                class="flex-1 rounded-none border-0 bg-transparent focus:ring-0 focus-visible:ring-0"
              />
              <Select v-model="fromCurrency">
                <SelectTrigger
                  class="w-[80px] rounded-none border-0 border-l bg-transparent focus:ring-0 focus-visible:ring-0"
                  :aria-label="$t('common.currency')"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="currency in fromCurrencyOptions"
                    :key="currency.value"
                    :value="currency.value"
                  >
                    <div class="flex items-center gap-2">
                      <CurrencyIcon :code="currency.label" size="sm" />
                      {{ currency.label }}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <!-- Swap Button -->
          <div class="flex justify-center py-3">
            <Button
              variant="outline-gray"
              size="icon"
              class="rounded-full"
              :aria-label="$t('common.swap_currencies_and_amounts')"
              @click="swapCurrencies"
            >
              <ArrowRightLeft class="h-4 w-4" />
            </Button>
          </div>

          <!-- To Currency -->
          <div class="space-y-2">
            <Label for="to-amount-mobile">{{ $t('common.will_receive') }}</Label>
            <div
              class="flex h-[2.25rem] items-center gap-0 overflow-hidden rounded-lg border border-gray-300 bg-gray-50"
            >
              <Input
                id="to-amount-mobile"
                v-model="toAmount"
                type="text"
                readonly
                class="flex-1 rounded-none border-0 bg-transparent focus:ring-0 focus-visible:ring-0"
              />
              <Select v-model="toCurrency">
                <SelectTrigger
                  class="w-[80px] rounded-none border-0 border-l bg-transparent focus:ring-0 focus-visible:ring-0"
                  :aria-label="$t('common.currency')"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="currency in toCurrencyOptions"
                    :key="currency.value"
                    :value="currency.value"
                  >
                    <div class="flex items-center gap-2">
                      <CurrencyIcon :code="currency.label" size="sm" />
                      {{ currency.label }}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <!-- Exchange Rate Info -->
      <div
        v-if="fromCurrency !== toCurrency"
        class="mt-6 rounded-lg border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-4"
      >
        <div class="text-center">
          <div class="mb-1 text-lg font-semibold text-gray-800">
            1 {{ fromCurrency.toUpperCase() }} =
            <span class="text-xl font-bold text-blue-600">
              {{ Number(getCurrentRate(fromCurrency, toCurrency) || 1).toFixed(4) }}
            </span>
            {{ toCurrency.toUpperCase() }}
          </div>
          <div class="flex items-center justify-center gap-1">
            <span
              class="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-sm font-medium text-green-700"
            >
              <div class="mr-1 h-2 w-2 rounded-full bg-green-400" />
              {{ bankName }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Rate Options: only show if both types are available -->
    <div v-if="hasRegularData && hasCashlessData" class="space-y-4">
      <div>
        <Label class="text-sm font-medium">{{
          $t('widget.bank_page.rates_converter.rate_type_label')
        }}</Label>
        <div class="mt-2 flex flex-wrap gap-2">
          <Button
            size="sm"
            :variant="rateType === 'regular' ? 'blue' : 'gray'"
            @click="rateType = 'regular'"
          >
            {{ $t('widget.bank_page.rates_converter.rate_type_cash') }}
          </Button>
          <Button
            size="sm"
            :variant="rateType === 'cashless' ? 'blue' : 'gray'"
            @click="rateType = 'cashless'"
          >
            {{ $t('common.cashless') }}
          </Button>
        </div>
      </div>
    </div>

    <div class="text-xs text-gray-500">
      {{ $t('widget.bank_page.rates_converter.disclaimer', { bankName }) }}
    </div>
  </div>
</template>
